import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// UI
import Table from "@/components/ui/table";
import type { TableColumn } from "@/types/component";

// Report Components
import ReportDetailLayout from "../../components/ReportDetailLayout";
import ReportFilterBar, {
  type ReportFilterState,
} from "../../components/ReportFilterBar";
import ReportKPICard from "../../components/ReportKPICard";
import ReportKpiGrid from "../../components/ReportKpiGrid";
import ReportSection from "../../components/ReportSection";
import ReportChart from "../../components/ReportChart";
import ReportExportButtons from "../../components/ReportExportButtons";

// Hooks
import { useReportData } from "../../hooks/useReportData";

// Services
import { reportsExtendedApi } from "../../services/reports-extended.api";
import { apiClient } from "@/lib/apiClient";
import { fetchDepartments } from "@/features/announcements/services/departments.api";

// Icons
import { User } from "@/components/icons";

// Styles
import styles from "./LoginActivityReportPage.module.scss";

// ---------------------------------------------------------------------------
// Inline types — backend hazır, reports-extended.api.ts'e ana asistan ekleyecek.
// ---------------------------------------------------------------------------
interface LoginActivityHourCell {
  dayOfWeek: number;
  hour: number;
  count: number;
}
interface LoginActivityDailyPoint {
  date: string;
  total: number;
  unique: number;
}
interface LoginActivityDeptBucket {
  departmentID: string | null;
  name: string;
  loginCount: number;
  uniqueUsers: number;
}
interface LoginActivityItem {
  userID: string;
  fullName: string;
  email: string | null;
  department: string | null;
  loginCount: number;
  distinctDays: number;
  lastLoginAt: string | null;
}
interface LoginActivityResponse {
  summary: {
    dau: number;
    wau: number;
    mau: number;
    totalLogins: number;
    avgLoginsPerActiveUser: number;
    peakHour: number;
    peakDayOfWeek: number;
  };
  daily: LoginActivityDailyPoint[];
  hourly: LoginActivityHourCell[];
  byDepartment: LoginActivityDeptBucket[];
  items: LoginActivityItem[];
  count: number;
}
interface LoginActivityParams {
  page?: number;
  limit?: number;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  departmentID?: string;
}

// ---------------------------------------------------------------------------
// Local fetcher — geçici. Ana asistan reports-extended.api.ts'e
// `getLoginActivity` ekleyecek; o zaman bu yerel fetcher silinebilir.
// ---------------------------------------------------------------------------
const fetchLoginActivity = async (
  params: LoginActivityParams,
  signal?: AbortSignal,
): Promise<LoginActivityResponse> => {
  const { data } = await apiClient.get("/api/admin/reports/login-activity", {
    params,
    signal,
  });
  return (data?.data ?? data) as LoginActivityResponse;
};

const PAGE_SIZE = 20;

// Türkçe gün etiketleri (hourly heatmap için: 0=Pazar)
const DAY_LABELS_TR = ["Pzr", "Pzt", "Sal", "Çar", "Per", "Cum", "Cts"];

const formatDateTime = (iso: string | null): string => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  } catch {
    return iso;
  }
};

const formatNumber = (n: number): string =>
  new Intl.NumberFormat("tr-TR").format(n);

const LoginActivityReportPage: React.FC = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<ReportFilterState>({});
  const [page, setPage] = useState(1);

  // Departments dropdown — ReportsPage / CertificatePipeline pattern
  const [departments, setDepartments] = useState<
    { id: string; name: string }[]
  >([]);

  useEffect(() => {
    let cancelled = false;
    fetchDepartments({ limit: 100 })
      .then((list) => {
        if (!cancelled) {
          setDepartments(list.map((d) => ({ id: d.id, name: d.name })));
        }
      })
      .catch(() => {
        if (!cancelled) setDepartments([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const params: LoginActivityParams = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      dateFrom: filter.dateFrom,
      dateTo: filter.dateTo,
      departmentID: filter.departmentID || undefined,
      search: filter.search?.trim() || undefined,
    }),
    [filter, page],
  );

  const { data, isLoading, error } = useReportData<
    LoginActivityResponse,
    LoginActivityParams
  >({
    fetcher: (p, signal) => fetchLoginActivity(p, signal),
    params,
  });

  const summary = data?.summary;
  const daily = data?.daily ?? [];
  const hourly = data?.hourly ?? [];
  const byDepartment = data?.byDepartment ?? [];
  const items = data?.items ?? [];
  const count = data?.count ?? 0;

  // ---- Heatmap series (7 row × 24 col) -----------------------------------
  const heatmapSeries = useMemo(() => {
    // index map: { `${dow}-${hour}`: count }
    const map = new Map<string, number>();
    hourly.forEach((c) => {
      map.set(`${c.dayOfWeek}-${c.hour}`, c.count);
    });
    // Apex bekliyor: en üst satır series[0] — UX olarak Pzt en üstte daha okunaklı
    // Dolayısıyla diziyi tersine çeviriyoruz (Cts → Pzt → ... → Pzr en altta)
    // Ancak Apex heatmap'in default davranışı: ilk eleman en altta. Bu yüzden
    // Pazartesi'nin en üstte görünmesi için seri sırası: Cts, Cum, Per, Çar, Sal, Pzt, Pzr.
    const orderedDays = [6, 5, 4, 3, 2, 1, 0]; // bottom→top
    return orderedDays.map((dow) => ({
      name: DAY_LABELS_TR[dow],
      data: Array.from({ length: 24 }, (_, h) => ({
        x: `${h.toString().padStart(2, "0")}:00`,
        y: map.get(`${dow}-${h}`) ?? 0,
      })),
    }));
  }, [hourly]);

  // ---- Department top-10 -------------------------------------------------
  const topDepartments = useMemo(
    () => [...byDepartment].sort((a, b) => b.loginCount - a.loginCount).slice(0, 10),
    [byDepartment],
  );

  // ---- Table columns -----------------------------------------------------
  const columns: TableColumn<LoginActivityItem>[] = useMemo(
    () => [
      {
        header: t("reports.loginActivity.tableUser"),
        accessor: (row) => (
          <div className={styles.userCell}>
            <span className={styles.userName}>{row.fullName}</span>
            {row.email && <span className={styles.userEmail}>{row.email}</span>}
          </div>
        ),
      },
      {
        header: t("reports.loginActivity.tableDepartment"),
        accessor: (row) =>
          row.department ? (
            row.department
          ) : (
            <span className={styles.muted}>—</span>
          ),
      },
      {
        header: t("reports.loginActivity.tableLoginCount"),
        accessor: (row) => (
          <span className={styles.numCell}>{formatNumber(row.loginCount)}</span>
        ),
      },
      {
        header: t("reports.loginActivity.tableDistinctDays"),
        accessor: (row) => (
          <span className={styles.numCell}>
            {formatNumber(row.distinctDays)}
          </span>
        ),
      },
      {
        header: t("reports.loginActivity.tableLastLogin"),
        accessor: (row) => formatDateTime(row.lastLoginAt),
      },
    ],
    [t],
  );

  // ---- Render ------------------------------------------------------------
  return (
    <ReportDetailLayout
      title={t("reports.loginActivity.title")}
      categoryLabel={t("reports.loginActivity.category")}
      description={t("reports.loginActivity.description")}
      exportSlot={
        <ReportExportButtons
          reportType="LOGIN_ACTIVITY"
          filters={params as unknown as Record<string, unknown>}
          filenameBase="giris-aktivite"
          exportFn={reportsExtendedApi.exportReport}
          disabled={isLoading}
        />
      }
      filterSlot={
        <ReportFilterBar
          value={filter}
          onChange={(next) => {
            setFilter(next);
            setPage(1);
          }}
          departments={departments}
        />
      }
    >
      {/* KPI grid */}
      <div className={styles.kpiGrid}>
        <ReportKpiGrid columns={4}>
          <ReportKPICard
            title={t("reports.loginActivity.kpiDau")}
            value={summary ? formatNumber(summary.dau) : "—"}
            icon={<User width={18} height={18} />}
          />
          <ReportKPICard
            title={t("reports.loginActivity.kpiWau")}
            value={summary ? formatNumber(summary.wau) : "—"}
            icon={<User width={18} height={18} />}
          />
          <ReportKPICard
            title={t("reports.loginActivity.kpiMau")}
            value={summary ? formatNumber(summary.mau) : "—"}
            icon={<User width={18} height={18} />}
            accent="success"
          />
          <ReportKPICard
            title={t("reports.loginActivity.kpiAvgLoginsPerActive")}
            value={
              summary ? summary.avgLoginsPerActiveUser.toFixed(1) : "—"
            }
            icon={<User width={18} height={18} />}
            footnote={
              summary
                ? t("reports.loginActivity.kpiAvgFootnote", {
                    total: formatNumber(summary.totalLogins),
                  })
                : undefined
            }
          />
        </ReportKpiGrid>
      </div>

      {/* Daily trend */}
      <ReportSection title={t("reports.loginActivity.sectionDailyTrend")}>
        <ReportChart
          type="line"
          isLoading={isLoading}
          isEmpty={daily.length === 0}
          emptyMessage={t("reports.loginActivity.noData")}
          series={[
            {
              name: t("reports.loginActivity.seriesTotalLogins"),
              data: daily.map((d) => d.total),
            },
            {
              name: t("reports.loginActivity.seriesUniqueUsers"),
              data: daily.map((d) => d.unique),
            },
          ]}
          options={{
            xaxis: { categories: daily.map((d) => d.date) },
            stroke: { curve: "smooth", width: 2 },
            colors: ["#4f46e5", "#10b981"],
          }}
        />
      </ReportSection>

      {/* Hourly heatmap */}
      <ReportSection title={t("reports.loginActivity.sectionHeatmap")}>
        <ReportChart
          type="heatmap"
          height={360}
          isLoading={isLoading}
          isEmpty={hourly.length === 0}
          emptyMessage={t("reports.loginActivity.noData")}
          series={heatmapSeries}
          options={{
            chart: { toolbar: { show: false } },
            dataLabels: { enabled: false },
            colors: ["#4f46e5"],
            plotOptions: {
              heatmap: {
                radius: 4,
                enableShades: true,
                shadeIntensity: 0.55,
              },
            },
            xaxis: { type: "category" },
          }}
        />
      </ReportSection>

      {/* Department bar */}
      <ReportSection title={t("reports.loginActivity.sectionByDepartment")}>
        <ReportChart
          type="bar"
          height={360}
          isLoading={isLoading}
          isEmpty={topDepartments.length === 0}
          emptyMessage={t("reports.loginActivity.noData")}
          series={[
            {
              name: t("reports.loginActivity.seriesLogin"),
              data: topDepartments.map((d) => d.loginCount),
            },
          ]}
          options={{
            chart: { toolbar: { show: false } },
            plotOptions: {
              bar: { horizontal: true, borderRadius: 4, barHeight: "70%" },
            },
            xaxis: { categories: topDepartments.map((d) => d.name) },
            colors: ["#4f46e5"],
          }}
        />
      </ReportSection>

      {/* Active users table */}
      <ReportSection title={t("reports.loginActivity.sectionActiveUsers")}>
        <Table
          columns={columns}
          data={items}
          isLoading={isLoading}
          getRowId={(row) => row.userID}
          totalRecords={count}
          currentPage={page}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          emptyMessage={
            error
              ? t("reports.loginActivity.errorLoading")
              : t("reports.loginActivity.noActiveUsers")
          }
        />
      </ReportSection>
    </ReportDetailLayout>
  );
};

export default LoginActivityReportPage;
